import type { Ref } from 'react';
import { Footprints } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCount } from '@/utils/number';
import type { JourneyOverlay } from '../hooks/useMoTravelDetail';
import { countJourneyStops, type JourneyEntry } from '../utils/journeyEntries';
import type { StopDetail } from '../utils/routeFocus';
import type { StartPlace } from '../utils/startPlaces';
import { MoJourneyEntryCard } from './MoJourneyEntryCard';
import { MoJourneyStepper } from './MoJourneyStepper';
import { MoDockedStartPlacePicker } from './MoDockedStartPlacePicker';

const DOCKED_CONTROL_CLASS = 'w-full max-w-none rounded-lg bg-card shadow-xs backdrop-blur-none';
const ENTRY_CARD_ENTER_CLASS =
  'shadow-sm motion-safe:animate-in motion-safe:fade-in-40 motion-safe:duration-150 motion-safe:ease-out';

interface MoMobileJourneyPanelProps {
  overlay: JourneyOverlay;
  journeyEntries: JourneyEntry[];
  startPlaces: StartPlace[];
  focusedEntry: JourneyEntry | null;
  focusedStopDetail: StopDetail | null;
  canStepJourney: boolean;
  canReturnToStartPicker: boolean;
  onStepSelect: (waypointKey: string) => void;
  onJourneyStart: () => void;
  onJourneyExit: () => void;
  onFocusClear: () => void;
  ref?: Ref<HTMLElement> | undefined;
}

export function MoMobileJourneyPanel({
  overlay,
  journeyEntries,
  startPlaces,
  focusedEntry,
  focusedStopDetail,
  canStepJourney,
  canReturnToStartPicker,
  onStepSelect,
  onJourneyStart,
  onJourneyExit,
  onFocusClear,
  ref,
}: MoMobileJourneyPanelProps) {
  if (focusedEntry) {
    return (
      <section
        ref={ref}
        aria-label="Journey step"
        className="flex flex-col gap-2 border-t bg-muted/40 p-3 lg:hidden"
      >
        {(canStepJourney || canReturnToStartPicker) && (
          <MoJourneyStepper
            entries={journeyEntries}
            activeKey={focusedEntry.key}
            onStepSelect={onStepSelect}
            onExit={onJourneyExit}
            canReturnToStartPicker={canReturnToStartPicker}
            className={DOCKED_CONTROL_CLASS}
          />
        )}
        <MoJourneyEntryCard
          key={focusedEntry.key}
          entry={focusedEntry}
          stopDetail={focusedStopDetail}
          onClose={onFocusClear}
          hasTintedBody
          className={ENTRY_CARD_ENTER_CLASS}
        />
      </section>
    );
  }

  if (overlay === 'startButton') {
    const stopCount = countJourneyStops(journeyEntries);
    return (
      <div className="border-t p-3 lg:hidden">
        <Button variant="outline" onClick={onJourneyStart} className="h-10 w-full gap-2">
          <Footprints className="size-4 text-primary" aria-hidden />
          Start journey
          <span className="font-normal text-muted-foreground">
            · {formatCount(stopCount, 'stop')}
          </span>
        </Button>
      </div>
    );
  }

  if (overlay === 'startPicker') {
    return (
      <div className="border-t bg-muted/40 px-3 py-2.5 lg:hidden">
        <MoDockedStartPlacePicker startPlaces={startPlaces} onStartSelect={onStepSelect} />
      </div>
    );
  }

  if (overlay === 'multiDayHint') {
    return (
      <p className="flex items-center gap-1.5 border-t px-3 py-2.5 text-xs text-muted-foreground lg:hidden">
        <Footprints className="size-3.5 shrink-0" aria-hidden />
        Tap a stop on the map, or pick a day, to step through its journey
      </p>
    );
  }

  return null;
}
